import {
  DEFAULT_CALLBACK_PATH,
  DEFAULT_ERROR_REDIRECT,
  DEFAULT_EXPIRY_BUFFER_SEC,
  DEFAULT_REFRESH_MAX_AGE,
  DEFAULT_ROUTES,
  DEFAULT_SCOPES,
  DEFAULT_SESSION_MAX_AGE,
  DEFAULT_STATE_MAX_AGE,
} from './constants.js';
import type { AppDirectAuthConfig, CookieOptions, ResolvedConfig } from './types.js';

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/$/, '');
}

function parseApplicationId(value?: string | number): number | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return null;
    return parsed;
  }
  if (!Number.isFinite(value)) return null;
  return value;
}

export function resolveConfig(input: AppDirectAuthConfig): ResolvedConfig {
  const issuerBaseUrl = normalizeBaseUrl(input.issuerBaseUrl);
  const appBaseUrl = normalizeBaseUrl(input.appBaseUrl);
  const callbackPath = input.callbackPath ?? DEFAULT_CALLBACK_PATH;
  const partitioned = input.cookieOptions?.partitioned ?? false;
  let secure =
    input.secureCookies ??
    input.cookieOptions?.secure ??
    process.env.NODE_ENV === 'production';
  let sameSite = input.cookieOptions?.sameSite ?? 'lax';

  if (partitioned) {
    secure = true;
    if (input.cookieOptions?.sameSite === undefined) {
      sameSite = 'none';
    }
  }

  const cookieOptions: Required<CookieOptions> = {
    sessionMaxAge: input.cookieOptions?.sessionMaxAge ?? DEFAULT_SESSION_MAX_AGE,
    refreshMaxAge: input.cookieOptions?.refreshMaxAge ?? DEFAULT_REFRESH_MAX_AGE,
    stateMaxAge: input.cookieOptions?.stateMaxAge ?? DEFAULT_STATE_MAX_AGE,
    secure,
    sameSite,
    path: input.cookieOptions?.path ?? '/',
    partitioned,
  };

  return {
    issuerBaseUrl,
    clientId: input.clientId,
    clientSecret: input.clientSecret,
    appBaseUrl,
    applicationId: parseApplicationId(input.applicationId),
    callbackPath,
    redirectUri: `${appBaseUrl}${callbackPath}`,
    scopes: input.scopes ?? [...DEFAULT_SCOPES],
    cookieOptions,
    expiryBufferSec: input.expiryBufferSec ?? DEFAULT_EXPIRY_BUFFER_SEC,
    routes: {
      login: input.routes?.login ?? DEFAULT_ROUTES.login,
      callback: input.routes?.callback ?? DEFAULT_ROUTES.callback,
      me: input.routes?.me ?? DEFAULT_ROUTES.me,
      jwt: input.routes?.jwt ?? DEFAULT_ROUTES.jwt,
      refresh: input.routes?.refresh ?? DEFAULT_ROUTES.refresh,
      logout: input.routes?.logout ?? DEFAULT_ROUTES.logout,
    },
    onErrorRedirect: input.onErrorRedirect ?? DEFAULT_ERROR_REDIRECT,
    endpoints: {
      authorizeUrl: `${issuerBaseUrl}/oauth2/authorize`,
      tokenUrl: `${issuerBaseUrl}/oauth2/token`,
      userinfoUrl: `${issuerBaseUrl}/oauth2/userinfo`,
      authTokenUrl: `${issuerBaseUrl}/auth/token`,
      authRefreshUrl: `${issuerBaseUrl}/auth/refresh`,
    },
  };
}

export function validateConfig(config: ResolvedConfig): void {
  if (!config.issuerBaseUrl || !config.clientId || !config.clientSecret || !config.appBaseUrl) {
    throw new Error('issuerBaseUrl, clientId, clientSecret, and appBaseUrl are required');
  }
  if (config.cookieOptions.partitioned && config.cookieOptions.sameSite !== 'none') {
    throw new Error('cookieOptions.partitioned requires cookieOptions.sameSite to be "none"');
  }
}
